import React from "react";
import Button from "../Button/Button";
import Input from "../Input/Input";
import List from "../List/List";
import { LicenseUnitSelector } from "../LicenseUnitSelector/LicenseUnitSelector";
import ModalComponent from "../Modal/ModalComponent";
import { useAuth } from "../../contexts/auth";
import { useUserStore } from "../../contexts/userStore";
import AccessGroupService from "../../services/AccessGroupService";
import "../LevelSelector/LevelSelector.css";
import "./AccessGroupSelector.css";
import { MdOutlineDelete } from "react-icons/md";

const GROUPS_PER_PAGE = 8;

function getGroupId(group) {
	return group?.originalId ?? group?.id;
}

function isSameGroup(groupA, groupB) {
	const idA = getGroupId(groupA);
	const idB = getGroupId(groupB);
	return (idA != null && idB != null && String(idA) === String(idB)) ||
		(groupA?.uuid && groupA.uuid === groupB?.uuid);
}

export function AccessGroupSelector({
	groups = [],
	onChange,
	disabled = false,
	title = "Grupos de acesso",
	className = "",
	style,
}) {
	const [availableGroups, setAvailableGroups] = React.useState([]);
	const [searchGroup, setSearchGroup] = React.useState("");
	const { selectedCondominio } = useUserStore();
	const [unitFilter, setUnitFilter] = React.useState(selectedCondominio || "");
	const [currentPage, setCurrentPage] = React.useState(1);
	const [maxPage, setMaxPage] = React.useState(1);
	const [isLoading, setIsLoading] = React.useState(true);
	const [isModalOpen, setIsModalOpen] = React.useState(false);
	const [pendingGroups, setPendingGroups] = React.useState({});
	const { user } = useAuth();
	const organizationId = user?.userData?.selectedOrganizationId;
	const selectableGroups = groups.filter(group => !group?.isTempEnabled);

	React.useEffect(() => {
		if (!selectedCondominio) return;
		setUnitFilter(selectedCondominio);
		setCurrentPage(1);
	}, [selectedCondominio]);

	React.useEffect(() => {
		let timeoutId;
		let controller;

		if (!organizationId) {
			setAvailableGroups([]);
			setIsLoading(false);
			return undefined;
		}

		setIsLoading(true);
		timeoutId = setTimeout(async () => {
			controller = new AbortController();
			const response = await AccessGroupService.listAccessGroupsPaginated(
				currentPage,
				searchGroup,
				organizationId,
				"createdAt",
				controller.signal,
				GROUPS_PER_PAGE,
				unitFilter || null,
			);

			if (controller.signal.aborted) return;
			if (response?.data?.items) {
				setAvailableGroups(response.data.items);
				setMaxPage(response.data.totalPages || 1);
			} else {
				setAvailableGroups([]);
				setMaxPage(1);
			}
			setIsLoading(false);
		}, 250);

		return () => {
			clearTimeout(timeoutId);
			controller?.abort();
		};
	}, [currentPage, searchGroup, unitFilter, organizationId]);

	const isSelected = group => selectableGroups.some(selected => isSameGroup(selected, group));
	const groupsToAdd = availableGroups.filter(group => !isSelected(group));
	const allPageGroupsSelected = groupsToAdd.length > 0 &&
		groupsToAdd.every(group => pendingGroups[String(getGroupId(group))]);

	const toggleGroup = (group, checked) => {
		const key = String(getGroupId(group));
		setPendingGroups(previous => {
			const updated = { ...previous };
			if (checked) updated[key] = group;
			else delete updated[key];
			return updated;
		});
	};

	const toggleAllOnPage = checked => {
		setPendingGroups(previous => {
			const updated = { ...previous };
			groupsToAdd.forEach(group => {
				const key = String(getGroupId(group));
				if (checked) updated[key] = group;
				else delete updated[key];
			});
			return updated;
		});
	};

	const confirmAddGroups = () => {
		const newGroups = Object.values(pendingGroups).filter(group => !isSelected(group));
		onChange?.([...groups, ...newGroups]);
		setPendingGroups({});
		setIsModalOpen(false);
	};

	const removeGroup = groupToRemove => {
		onChange?.(groups.filter(group => !isSameGroup(group, groupToRemove)));
	};

	const groupColumns = [
		{ collumnLabel: "Nome", index: "name", type: "text" },
		{ collumnLabel: "Descrição", index: "description", type: "text" },
		{
			collumnLabel: "Selecionar",
			index: "selection",
			columnClassName: "w-24!",
			customHeader: () => (
				<label className="flex items-center justify-center gap-2">
					<input
						type="checkbox"
						aria-label="Selecionar todos os grupos desta página"
						checked={allPageGroupsSelected}
						disabled={groupsToAdd.length === 0}
						onChange={({ target }) => toggleAllOnPage(target.checked)}
					/>
					<span>Todos</span>
				</label>
			),
			custom: ({ row }) => {
				const alreadySelected = isSelected(row);
				const rowKey = String(getGroupId(row));
				return (
					<div className="flex justify-center">
						<input
							type="checkbox"
							aria-label={`Selecionar grupo ${row.name}`}
							checked={alreadySelected || !!pendingGroups[rowKey]}
							disabled={alreadySelected}
							onChange={({ target }) => toggleGroup(row, target.checked)}
						/>
					</div>
				);
			},
		},
	];

	return (
		<div className={`levels-selector access-group-selector ${className}`} style={style}>
			<div className="input-tooltip-container mb-3!">{title}</div>
			<div className="selected-levels-list w-full! mb-0!">
				{isLoading && (
					<div className="flex items-center justify-center gap-2 py-4! text-(--textColor) opacity-60">
						<span className="inline-block size-4 animate-spin rounded-full border-2 border-current border-r-transparent" />
						<span>Carregando grupos de acesso...</span>
					</div>
				)}
				{selectableGroups.length > 0 ? selectableGroups.map(group => (
					<div key={getGroupId(group) ?? group.uuid} className="selected-level-item">
						<div className="selected-level-item-info">
							<h3>{group.name ?? group.label}</h3>
						</div>
						{!disabled && (
							<button
								type="button"
								className="btn-delete-item"
								aria-label={`Remover grupo ${group.name ?? group.label}`}
								onClick={() => removeGroup(group)}
							>
								<MdOutlineDelete size={18} />
							</button>
						)}
					</div>
				)) : (
					!isLoading && <div className="no-levels-selected">Nenhum grupo selecionado</div>
				)}
			</div>
			{!disabled && (
				<div className="container-buttons mt-1.5!">
					<Button
						label="Adicionar grupo"
						action="add"
						className="button-link"
						onClick={() => {
							setPendingGroups({});
							setIsModalOpen(true);
						}}
					/>
				</div>
			)}
			<ModalComponent
				modalIsOpen={isModalOpen}
				modalText="Adicionar grupos de acesso"
				buttonLabel1="Confirmar"
				buttonLabel2="Cancelar"
				handleConfirm={confirmAddGroups}
				handleCancel={() => {
					setPendingGroups({});
					setIsModalOpen(false);
				}}
			>
				<div className="w-190 max-w-[85vw] flex flex-col gap-4">
					<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
						<LicenseUnitSelector
							label="Licença"
							unitId={unitFilter}
							setUnitId={unitId => {
								setCurrentPage(1);
								setUnitFilter(unitId ?? "");
							}}
						/>
						<Input
							labelText="Buscar grupo"
							value={searchGroup}
							type="text"
							placeholder="Buscar"
							onChange={({ target }) => {
								setCurrentPage(1);
								setSearchGroup(target.value);
							}}
						/>
					</div>
					<List
						isLoading={isLoading}
						selectableRows={false}
						collumsObjIndex={groupColumns}
						data={availableGroups}
						changePage={setCurrentPage}
						maxPage={maxPage}
						rowsPerPage={GROUPS_PER_PAGE}
					/>
				</div>
			</ModalComponent>
		</div>
	);
}
